import { useContext, useEffect, useState } from 'react';
import { Button, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { datePickerFormat } from 'helpers';
import { EstateResultItem, EstateResultItem_OneViewBankruptcyInput } from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';

import {
  CustomResultItemField,
  handleNewCustomFieldButtonClick,
  parseCustomFieldsJson,
  removeCustomField,
  updateCustomField,
} from '../utils/custom-result-item-field';

import NewEssentialData from './new-essential-data';

type Props = {
  estateResultItem?: EstateResultItem;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setEssentialData: Function;
};

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '3px',
    padding: '2px 10px',
  },
  container: {
    paddingTop: '10px',
  },
  dateField: {
    paddingTop: '10px',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
  inputField: {
    textAlign: 'left',
    width: '100%',
  },
  selectContainer: {
    textAlign: 'left',
  },
  title: {
    paddingTop: '10px',
  },
}));

export default function AddBankruptcyItem({ setEssentialData, estateResultItem }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);

  const [bankruptcyInput, setBankruptcyInput] = useState<EstateResultItem_OneViewBankruptcyInput>({
    customerNumber: 'N/A',
    caseNumber: 'N/A',
    type: 'Unknown bankruptcy type',
    filerCode: 'N/A',
    description: 'No description provided',
    status: 'N/A',
    dateFiled: '',
    dateReported: '',
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [customFields, setCustomFields] = useState<CustomResultItemField[]>([]);
  const [selectedDateFiled, setSelectedDateFiled] = useState<Date>();
  const [selectedDateReported, setSelectedDateReported] = useState<Date>();
  const [selectedBankruptcyCode, setSelectedBankruptcyCode] = useState('');

  const fields = ['Customer Number', 'Case Number', 'Description', 'Filer Code', 'Status'];
  const fieldKeys = ['customerNumber', 'caseNumber', 'description', 'filerCode', 'status'];

  const bankruptcyCodes = {
    A: 'DISCHARGED CH-7 The amount owed was included in the “order of relief.” Debtor no longer liable for debts listed in “order of relief.”',
    C: 'CH-13 FILED Adjustment of debts of an individual with consistent income. Debtor petitions the court for permission to pay a percent of his income over a period of years until debt is satisfied (usually not over three years).',
    D: 'CH-11 FILED Business Reorganization. Business debtor is granted relief from payment under terms of initial contract reorganization period.',
    E: 'DISMSD/CLSD CH11 Petition for reorganization of debt has been withdrawn by debtor or honored - subject liable for debts.',
    F: 'DISCHARGED CH-11 Petition by debtor for complete relief of all debts is honored. Debtor no longer liable for debts listed in “order of relief.”',
    G: 'CH-12 FILED The Chapter 12 plan applies only to family farmers who have regular annual incomes sufficient to make payments under a proposed plan. Payment under the plan must be completed in three years. In certain situations, payment of no longer than five years is permitted.',
    H: 'DISCHARGED CH-12 A discharge is entered after the completion of all payments under the plan. However, certain payments on some long term claims, which are due after the last payment under the plan, will continue after the date of discharge.',
    I: 'INVOLUNTARY CH-7 The debtor is forced into bankruptcy by the petition of a sufficient number of his creditors.',
    J: 'DISMSD/CLSD CH12 The court may dismiss the plan or terminate the plan for various reasons, including unreasonable delays, gross mismanagement, non payment of any fees and charges, failure to file a plan in a timely manner, failure to make timely payments required by a confirmed plan, denial of confirmation or request made for additional time, and so forth.',
    K: 'DISMSD/CLSD CH13 The petition by debtor for permission to pay a percent of his income over a period of years has been withdrawn by debtor or has not been followed by debtor. Debtor remains liable for his debts under initial terms of the contract.',
    L: 'DISCHARGED CH-13 Plan by debtor to pay percent of income over a period of years has been completed. Debtor no longer liable for debts listed in payment plan.',
    M: 'DISMSD/CLSD CH7 The bankruptcy petition has been withdrawn by or has not been honored by the court. Subject remains liable for his debts under the terms of the initial contract.',
    V: "VOLUNTARY CH-7 Bankruptcy proceeding is initiated by the debtor's own petition to be declared bankrupt and have benefit of the law.",
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (e: any) => {
    const name: string = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    bankruptcyInput[name as keyof EstateResultItem_OneViewBankruptcyInput] = value;
    setSelectedBankruptcyCode(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (e: any) => {
    const name: string = e.target.name;
    const newInput = { ...bankruptcyInput };
    newInput[name as keyof EstateResultItem_OneViewBankruptcyInput] = e.target.value;
    setBankruptcyInput(newInput);
    setEssentialData(bankruptcyInput);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateInputChange = (e: any, name: string) => {
    //const newEssentialData: any = bankruptcyInput;
    const date = new Date(e);
    const formattedDate = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}/${
      date.getDate() < 10 ? '0' : ''
    }${date.getDate()}/${date.getFullYear()}`;
    bankruptcyInput[name as keyof EstateResultItem_OneViewBankruptcyInput] = formattedDate;
    if (name === 'dateFiled') {
      setSelectedDateFiled(e);
    } else {
      setSelectedDateReported(e);
    }
    setEssentialData(bankruptcyInput);
  };

  const mapExistingEstateReusltItemData = (estateResultItem: EstateResultItem) => {
    const essentialData = JSON.parse(estateResultItem.essentialData!);
    const newInput: EstateResultItem_OneViewBankruptcyInput = {
      customerNumber: essentialData.customerNumber || 'N/A',
      caseNumber: essentialData.caseNumber || 'N/A',
      type: essentialData.type || 'Unknown bankruptcy type',
      filerCode: essentialData.filerCode || 'N/A',
      description: essentialData.description || 'No description provided',
      status: essentialData.status || 'N/A',
      dateFiled: essentialData.dateFiled || '',
      dateReported: essentialData.dateReported || '',
      customFields: essentialData.customFields || {},
    };
    setBankruptcyInput(newInput);
    setSelectedBankruptcyCode(newInput.type);
    setSelectedDateReported(new Date(newInput.dateReported) || '');
    setSelectedDateFiled(new Date(newInput.dateFiled) || '');
    setCustomFields(parseCustomFieldsJson(essentialData.customFields));
  };

  useEffect(() => {
    const essentialData: any = {};
    customFields.forEach((customField) => {
      essentialData[`customField${customField.id}`] = {
        id: customField.id,
        name: customField.field,
        value: customField.value,
      };
    });
    bankruptcyInput.customFields = essentialData;
    setEssentialData(bankruptcyInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, bankruptcyInput]);

  useEffect(() => {
    if (estateResultItem) {
      mapExistingEstateReusltItemData(estateResultItem);
    }
  }, [estateResultItem]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {context.isEditingEstateResultItem ? 'Edit Bankruptcy History' : 'Add Bankruptcy History'}
      </Typography>
      <Grid className={classes.container}>
        <Grid>
          {fields.map((field, index) => {
            return (
              <Grid className={classes.container}>
                <Grid>
                  <TextField
                    className={classes.inputField}
                    fullWidth
                    name={`${fieldKeys[index]}`}
                    label={
                      fieldKeys[index] !== 'filerCode'
                        ? `${field}`
                        : 'Individual, Business, or Spouse'
                    }
                    value={
                      context.isEditingEstateResultItem
                        ? bankruptcyInput[
                            `${fieldKeys[index] as keyof EstateResultItem_OneViewBankruptcyInput}`
                          ]
                        : null
                    }
                    variant="outlined"
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            );
          })}
          <Grid className={classes.container}>
            <Grid className={classes.selectContainer}>
              <Typography variant="caption">Bankruptcy Type</Typography>
              <Select
                fullWidth
                autoWidth={false}
                variant="outlined"
                name="type"
                value={selectedBankruptcyCode}
                onChange={handleSelectChange}
              >
                {Object.keys(bankruptcyCodes).map((key) => {
                  return (
                    <MenuItem
                      key={key}
                      value={bankruptcyCodes[key as keyof typeof bankruptcyCodes]}
                    >
                      <Typography noWrap>
                        {bankruptcyCodes[key as keyof typeof bankruptcyCodes]}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classes.dateField}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption">Date Filed</Typography>
            <DatePicker
              orientation="landscape"
              value={selectedDateFiled}
              minDate={selectedDateReported}
              onChange={(e) => handleDateInputChange(e, 'dateFiled')}
              format={datePickerFormat}
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption">Date Reported</Typography>
            <DatePicker
              orientation="landscape"
              value={selectedDateReported}
              maxDate={selectedDateFiled}
              onChange={(e) => handleDateInputChange(e, 'dateReported')}
              format={datePickerFormat}
              className={classes.inputField}
            />
          </Grid>
        </Grid>
        <Typography variant="h5" className={classes.title}>
          Custom Fields
        </Typography>
        <Grid className={classes.container}>
          <Grid item xs={12} sm={12}>
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              customFields.map((customField: CustomResultItemField, index: number) => {
                return (
                  <NewEssentialData
                    key={index}
                    customField={customField}
                    updateCustomField={(updatedCustomField: CustomResultItemField) => {
                      updateCustomField(updatedCustomField, customFields, setCustomFields);
                    }}
                    handleRemoveClick={() => {
                      removeCustomField(customField, customFields, setCustomFields);
                    }}
                  />
                );
              })
            }
          </Grid>
        </Grid>
        <div className={classes.container}>
          {!context.isEditingEstateResultItem && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.button}
              onClick={() => {
                handleNewCustomFieldButtonClick(customFields, setCustomFields);
              }}
            >
              + Add new field
            </Button>
          )}
        </div>
      </Grid>
    </>
  );
}
